import { useCallback } from 'react';

import useWebSocket from 'react-use-websocket';

import { globalStore } from '@store';

import { ClientMessage, BeastPayload, beastPayloadSchema, CLIENT_MESSAGE } from '../message/schema';
import { isConnectedToBeastAtom } from '../state/atoms';

export type SocketConnectionHookArgs = {
    messageEndpoint: string;
    device: 'kiosk' | 'floor';
    onMessage: (data: BeastPayload) => void;
};

export type SocketConnectionHookReturns = {
    sendMessage: (message: ClientMessage) => void;
    sendActivityId: (activityId: number | null) => void;
};

const isSecureSockets = import.meta.env['VITE_PUBLIC_UNSECURE_SOCKETS'] !== 'true';
const socketPrefix = isSecureSockets ? 'wss://' : 'ws://';

const STATUS_MESSAGE = {
    kiosk: CLIENT_MESSAGE.KIOSK_STATUS,
    floor: CLIENT_MESSAGE.FLOOR_STATUS,
} as const;

function getSocketURL(messageEndpoint: string, clientName: string): string {
    return `${socketPrefix}${messageEndpoint}/api/v1/ws/${clientName}`;
}

export const useSocketConnection = ({
    messageEndpoint,
    device,
    onMessage,
}: SocketConnectionHookArgs): SocketConnectionHookReturns => {
    const handleOpen = useCallback(() => {
        globalStore.set(isConnectedToBeastAtom, true);
    }, []);

    const { sendJsonMessage } = useWebSocket(getSocketURL(messageEndpoint, device), {
        onOpen: handleOpen,
        onClose: handleClose,
        onMessage: handleMessage,
        shouldReconnect: () => true,
        reconnectAttempts: 1000,
        reconnectInterval: 2000,
    });

    const sendActivityId = useCallback(
        (activityId: number | null) => {
            const id = activityId !== null && activityId >= 0 ? activityId : null;
            sendJsonMessage({
                type: STATUS_MESSAGE[device],
                content: { activity_id: id },
            });
        },
        [device, sendJsonMessage],
    );

    /**
     * Cannot memo, since it references sendJsonMessage and is passed to the useWebSocket as a reference,
     * so it needs to be hoisted
     */
    function handleMessage(message: MessageEvent<string>) {
        if (message.type !== 'message') return;

        const parsed = beastPayloadSchema.safeParse(JSON.parse(message.data));
        if (!parsed.success) {
            console.error('WebSocket payload validation failed:', parsed.error);
            return;
        }

        const data = parsed.data;
        if (data.event_id) {
            sendJsonMessage({ type: CLIENT_MESSAGE.EVENT_ACK, event_id: data.event_id });
        }
        onMessage(data);
    }

    function handleClose() {
        sendActivityId(null);
        console.warn('Disconnecting from beast');
        globalStore.set(isConnectedToBeastAtom, false);
    }

    return { sendMessage: sendJsonMessage, sendActivityId };
};
